import * as React from "react";
import { useNavigate } from "react-router";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import type { MembershipProject } from "@zworks/console-contracts";
import { SettingsView } from "@/pages/settings";
import {
  DEFAULT_SETTING_SLUG,
  settingSectionForSlug,
  visibleSettingSections,
} from "@/pages/settings-sections";

/**
 * The settings modal opened from the profile menu or Cmd+,. It renders the same two-column view
 * as the page, dismisses with Esc or the close button through the dialog primitive, and offers
 * "Open as page" to move to the deep link.
 */
export function SettingsDialog({
  project,
  open,
  onOpenChange,
  initialSection,
}: {
  project: MembershipProject | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSection?: string;
}) {
  const navigate = useNavigate();
  const visible = project ? visibleSettingSections(project.role, "dialog") : [];
  const requested = settingSectionForSlug(initialSection);
  const fallback = settingSectionForSlug(DEFAULT_SETTING_SLUG)!;
  const resolved =
    requested && visible.some((entry) => entry.slug === requested.slug)
      ? requested
      : (visible.find((entry) => entry.slug === fallback.slug) ?? visible[0] ?? fallback);

  function openAsPage(next: string) {
    onOpenChange(false);
    navigate(`/settings/${next}`);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100svh-4rem)] w-[min(64rem,calc(100%-2rem))] overflow-y-auto">
        <DialogTitle className="sr-only">Settings</DialogTitle>
        <DialogDescription className="sr-only">
          Project and account settings. Every section has its own page.
        </DialogDescription>
        {project && open ? (
          <SettingsDialogBody
            key={`${project.id}-${resolved.slug}-${String(open)}`}
            project={project}
            initialSlug={resolved.slug}
            onOpenAsPage={openAsPage}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The dialog body, mounted fresh on every open so the section resets to the requested one.
 * Keyed by the resolved slug above, so no effect is needed to synchronise state.
 */
function SettingsDialogBody({
  project,
  initialSlug,
  onOpenAsPage,
}: {
  project: MembershipProject;
  initialSlug: string;
  onOpenAsPage: (slug: string) => void;
}) {
  const [slug, setSlug] = React.useState(initialSlug);
  const active = settingSectionForSlug(slug) ?? settingSectionForSlug(DEFAULT_SETTING_SLUG)!;

  return (
    <SettingsView
      key={`${project.id}-${slug}`}
      project={project}
      active={active}
      headingLevel="dialog"
      surface="dialog"
      onOpenAsPage={onOpenAsPage}
      onSelectSection={setSlug}
    />
  );
}
